import { useTranslation } from 'react-i18next';
import compact from 'lodash/fp/compact';
import capitalize from 'lodash/fp/capitalize';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import { LargeInfoCard, SmallInfoCard, Title } from '@bloomays-lib/ui.shared';
import { ApolloGqlJobFunnel, DealType } from '@bloomays-lib/types.shared';

export type JobResumeProps = {
  job: ApolloGqlJobFunnel;
};

const JobResume = ({ job }: JobResumeProps) => {
  const { t } = useTranslation('talentBoard');
  const isCDI = Boolean(job.dealType) && [DealType.CDI_PEOPLE, DealType.CDI_TECH].includes(job.dealType as DealType);
  return (
    <Container>
      <Title textTitle={job.name} variant="heading5" sx={{ textAlign: 'left' }} />
      <KpisContainer>
        <Grid container spacing={{ xs: 0, sm: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="calendar"
              title={isCDI ? t('kpis.contractBeginDate') : t('kpis.missionBeginDate')}
              value={job.expectedStartDate ?? t('notSpecified')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="people"
              title={isCDI ? t('kpis.trialDuration') : t('kpis.missionDuration')}
              value={job.duration ? t('kpis.missionDurationInMonths', { count: job.duration }) : t('notSpecified')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="money"
              title={isCDI ? t('kpis.package') : t('kpis.tjm')}
              value={job.maxClientBudget ? `${job.maxClientBudget} €` : t('notSpecified')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="puzzle"
              title={t('kpis.seniority')}
              value={job.minYearsXP ? `${job.minYearsXP} ans` : t('notSpecified')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="building"
              title={t('kpis.rhythm')}
              value={
                job.onSiteDaysPerWeek
                  ? t('kpis.rhythmInDaysPerWeek', { count: job.onSiteDaysPerWeek })
                  : t('notSpecified')
              }
              caption={
                job.remoteDaysPerWeek
                  ? t('kpis.remoteDaysPerWeekCaption', { count: job.remoteDaysPerWeek })
                  : t('notSpecified')
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SmallInfoCard
              iconName="location"
              title={t('kpis.location')}
              value={job.location ? capitalize(job.location) : t('notSpecified')}
            />
          </Grid>
        </Grid>
      </KpisContainer>

      <LargeInfoCard
        title={t('jobResume.scorecard')}
        color="primary"
        iconName="fire"
        type="list"
        listItems={compact([job.scoreCard1, job.scoreCard2, job.scoreCard3, job.scoreCard4])}
      />
      <LargeInfoCard
        title={t('jobResume.description')}
        color="secondary"
        iconName="text"
        type="text"
        text={job.details ?? t('notSpecified')}
      />
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    gap: '12px',
  },
}));

const KpisContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    backgroundColor: theme.palette.neutral.white,
    borderRadius: '10px',
  },
}));

export default JobResume;
